<template>
	<v-card data-testid="column-filter" class="menu-card-width" >
		<v-list data-testid="column-filter__header-list" class="py-0">
			<v-list-item data-testid="column-filter__header" class="px-3 py-2 tableHeaderBg">
				<v-list-item-content data-testid="column-filter__header-content" class="py-0">
					<v-list-item-title data-testid="column-filter__header-title" class="d-flex justify-space-between align-center">
						<span data-testid="column-filter__title-text" class="font-weight-bold">
							<template v-if="filterStage === 0">Column Actions</template>
							<template v-else-if="filterStage === 1">Filter By</template>
						</span>
						<!-- <v-btn data-testid="column-filter__back-btn" v-if="filterStage > 0" icon @click="filterStage = 0" class="mr-1">
							<v-icon data-testid="column-filter__back-icon">mdi-arrow-left</v-icon>
						</v-btn> -->
					</v-list-item-title>
				</v-list-item-content>
				<v-btn data-testid="column-filter__close-btn" icon @click="onCloseClick">
					<v-icon data-testid="column-filter__close-icon">mdi-close</v-icon>
				</v-btn>
			</v-list-item>
		</v-list>
		<v-divider data-testid="column-filter__header-divider"></v-divider>

		<v-list data-testid="column-filter__content-list" class="py-0">
			<v-list-item data-testid="column-filter__filter-option" v-if="filterStage === 0" @click="openFilter" class="px-3 py-2">
				<v-list-item-icon data-testid="column-filter__filter-icon-wrapper" class="mr-2">
					<v-icon data-testid="column-filter__filter-icon">mdi-filter</v-icon>
				</v-list-item-icon>
				<v-list-item-title data-testid="column-filter__filter-title"><span>Filter by</span></v-list-item-title>
				<v-list-item-action data-testid="column-filter__filter-action">
					<v-icon data-testid="column-filter__filter-chevron">mdi-chevron-right</v-icon>
				</v-list-item-action>
			</v-list-item>

			<!-- Date Range Filter Panel -->
			<DateRangeFilter
				v-if="filterStage === 1 && isDateRangeType"
				:value="currentDateRange"
				@apply="onRangeApply"
				@close="onCloseClick"
				data-testid="column-filter__date-range-filter"
			/>

			<!-- Number Range Filter Panel -->
			<NumberRangeFilter
				v-else-if="filterStage === 1 && isNumberRangeType"
				:value="currentNumberRange"
				:data-range="columnDataRange"
				@apply="onRangeApply"
				@close="onCloseClick"
				data-testid="column-filter__number-range-filter"
			/>

			<!-- Standard Multi-Select Filter Panel -->
			<div data-testid="column-filter__filter-panel" v-else-if="filterStage === 1" class="px-3 py-2">
				<v-text-field
					data-testid="column-filter__search-input"
					v-model="searchText"
					label="Search"
					test-id="filter-search-input"
					dense
					outlined
					hide-details
					prepend-inner-icon="mdi-magnify"
					clearable
					clear-icon="mdi-close-circle"
					@click:clear="searchText=''"
					class="mb-2"
				/>

				<v-divider data-testid="column-filter__search-divider" class="my-2" />
				<div data-testid="column-filter__selected-count" class="d-flex align-center mb-2">
					<v-chip data-testid="column-filter__selected-chip" class="ml-0 mr-2 black--text" label outlined>
						{{ selectedCheckboxCount }} <span class="ml-1"> Selected</span>
					</v-chip>
				</div>
				<div data-testid="column-filter__items-scroll" class="filter-list-scroll">
					<FilterSelectAllCheckbox
						v-if="selectAllScopeItems.length > 0"
						test-id="column-filter__selectall-checkbox"
						class="pl-0"
						:options="selectAllScopeItems"
						:value="selectAllScopeSelectedValues"
						@input="onSharedSelectAllInput"
					/>

					<v-list-item
						v-for="item in pinnedItems"
						:key="`pinned-${getItemKey(item)}`"
						dense
						class="pl-0 column-filter__item column-filter__item--pinned"
						data-testid="column-filter__pinned-item"
					>
						<v-checkbox
							color="primary"
							class="mt-0 column-filter__item-checkbox"
							hide-details
							:input-value="item.status"
							@change="() => toggleItem(item)"
						>
							<template v-slot:label>
								<span
									class="column-filter__item-label-wrap"
									:class="{ 'column-filter__item-label-wrap--actor': item.actor === 'system' }"
								>
									<FcSystemActorAvatar
										v-if="item.actor === 'system'"
										:size="24"
										data-test-id="column-filter__pinned-system-avatar"
									/>
									<span
										class="column-filter__item-label"
										:title="formatItemLabel(item)"
									>{{ formatItemLabel(item) }}</span>
									<span
										v-if="item.sublabel"
										class="column-filter__item-sublabel"
									>{{ item.sublabel }}</span>
								</span>
							</template>
						</v-checkbox>
					</v-list-item>

					<v-list-item
						v-for="item in displayedColumnItems"
						:key="getItemKey(item)"
						data-testid="column-filter__item"
						dense
						class="pl-0 column-filter__item"
					>
						<v-checkbox
							data-testid="column-filter__item-checkbox"
							color="primary"
							class="mt-0 column-filter__item-checkbox"
							hide-details
							:input-value="item.status"
							@change="() => toggleItem(item)">
						<template v-slot:label>
							<span
								class="column-filter__item-label"
								:title="formatItemLabel(item)"
							>{{ formatItemLabel(item) }}</span>
						</template>
					</v-checkbox>
					</v-list-item>
					<div
						v-if="displayedColumnItems.length === 0"
						data-testid="column-filter__empty"
						class="text-center py-4 grey--text"
					>
						No results found
					</div>
				</div>

				<v-divider data-testid="column-filter__footer-divider" class="my-2" />
				<div data-testid="column-filter__footer" class="d-flex justify-end mt-1" v-if="showApplyButton || showSaveButton">
					<!-- Apply Button: applies filter without persisting -->
					<v-btn
						data-testid="column-filter__apply-btn"
						v-if="showApplyButton"
						class="mr-2 primaryBtn"
						@click="onApplyClick"
					><span>Apply</span></v-btn>
					<!-- Save Button: persists filter to backend -->
					<v-tooltip v-if="showSaveButton" top content-class="tooltip-top">
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								data-testid="column-filter__save-btn"
								v-bind="attrs"
								v-on="on"
								class="secondaryBtn"
								@click="onSaveClick"
							><span>Save</span></v-btn>
						</template>
						<span>Filter will be saved to your account</span>
					</v-tooltip>
				</div>
			</div>
		</v-list>
	</v-card>
</template>
<script>
import './ColumnFilter.scss';
import DateRangeFilter from './DateRangeFilter.vue';
import NumberRangeFilter from './NumberRangeFilter.vue';
import FilterSelectAllCheckbox from '@/views/common/FilterSelectAllCheckbox.vue';
import FcSystemActorAvatar from '@/modules/financialclose/components/FcSystemActorAvatar/FcSystemActorAvatar.vue';
import { applyStatusSelectAll } from '@/views/common/filterSelectAllUtils';

export default {
	name: 'ColumnFilter',
	components: { DateRangeFilter, NumberRangeFilter, FilterSelectAllCheckbox, FcSystemActorAvatar },
	props: {
		header: { type: Object, required: true },
		tableData: { type: Array, required: true },
		selectedValues: { type: [Array, Object], required: true },
		noneSentinel: { type: String, default: 'NONE_SELECTED' },
		/**
		 * @deprecated Use showSaveButton instead. Kept for backward compatibility.
		 */
		enableSaveOption: { type: Boolean, default: false },
		/**
		 * Whether to show the Apply button (applies filter without persisting)
		 */
		showApplyButton: { type: Boolean, default: true },
		/**
		 * Whether to show the Save button (persists filter to backend)
		 */
		showSaveButton: { type: Boolean, default: false },
		/**
		 * Auto-open directly to filter stage (skip Column Actions menu).
		 * Use when only filter is available (no other actions like sort).
		 */
		autoOpen: { type: Boolean, default: false },
		/**
		 * Whether the parent menu is currently open.
		 * Used to reset filterStage when menu reopens.
		 */
		isOpen: { type: Boolean, default: false },
		/**
		 * When true, retain known filter options if tableData shrinks while a column
		 * filter is active (tabledata-only mode). Disabled when parent supplies
		 * a stable filterOptionsData source.
		 */
		preserveOptionsOnFilter: { type: Boolean, default: true },
		/**
		 * Optional pinned rows prepended below Select all. Included in Select all / Deselect all.
		 * Falls back to header.pinnedFilterItems when omitted.
		 */
		pinnedFilterItems: { type: Array, default: null },
	},
	data() {
		return {
			filterStage: 0,
			searchText: '',
			baseItems: [], // [{ value, status }]
			pinnedItems: [], // [{ value, label, sublabel?, status }]
			userToggled: {}, // tracks items user explicitly toggled during this session
		};
	},
	computed: {
		resolvedPinnedFilterItems() {
			if (Array.isArray(this.pinnedFilterItems)) return this.pinnedFilterItems;
			return Array.isArray(this.header?.pinnedFilterItems) ? this.header.pinnedFilterItems : [];
		},
		/**
		 * Computes the min/max of the column values from tableData (for number-range type)
		 */
		columnDataRange() {
			if (!this.isNumberRangeType) return { min: null, max: null };
			const col = this.header.value;
 			let min = null;
 			let max = null;
 			this.tableData.forEach(row => {
 				const n = parseFloat(row[col]);
 				if (!isNaN(n)) {
 					min = min === null ? n : Math.min(min, n);
 					max = max === null ? n : Math.max(max, n);
 				}
 			});
 			return { min, max };
		},
		/**
		 * Whether this header uses date-range filter type
		 */
		isDateRangeType() {
			return this.header && this.header.type === 'date-range';
		},
		/**
		 * Whether this header uses number-range filter type
		 */
		isNumberRangeType() {
			return this.header && this.header.type === 'number-range';
		},
		/**
		 * Whether this header uses any range filter type (date or number)
		 */
		isRangeType() {
			return this.isDateRangeType || this.isNumberRangeType;
		},
		/**
		 * Current date range value from selectedValues for DateRangeFilter
		 */
		currentDateRange() {
			if (!this.isDateRangeType) return { from: null, to: null };
			const val = this.selectedValues;
			if (val && typeof val === 'object' && !Array.isArray(val)) return val;
			return { from: null, to: null };
		},
		/**
		 * Current number range value from selectedValues for NumberRangeFilter
		 */
		currentNumberRange() {
			if (!this.isNumberRangeType) return { min: null, max: null };
			const val = this.selectedValues;
			if (val && typeof val === 'object' && !Array.isArray(val)) return val;
			return { min: null, max: null };
		},
		/**
		 * Whether a search filter is currently active
		 */
		isSearchActive() {
			return (this.searchText || '').trim().length > 0;
		},
		/**
		 * Items to display - filtered by search text, sorted with selected first
		 */
		displayedColumnItems() {
			const term = (this.searchText || '').toLowerCase().trim();
			const items = !term
				? this.baseItems
				: this.baseItems.filter(i => {
					const label = (i && i.label != null) ? i.label : this.getFilterLabel(i?.value);
					const haystack = (label || '').toString().toLowerCase();
					return haystack.includes(term);
				});
			return items.slice().sort((a, b) => {
				if (a.status === b.status) return 0;
				return a.status ? -1 : 1; // selected first
			});
		},
		/**
		 * Count of selected items among currently displayed (visible) items
		 */
		selectedCheckboxCount() {
			const pinnedSelected = this.pinnedItems.filter(i => i.status).length;
			const displayedSelected = this.displayedColumnItems.filter(i => i.status).length;
			return pinnedSelected + displayedSelected;
		},
		/**
		 * Items impacted by Select all (scoped to search results when searching)
		 */
		selectAllScopeItems() {
			const columnItems = this.isSearchActive ? this.displayedColumnItems : this.baseItems;
			return [...this.pinnedItems, ...columnItems];
		},
		selectAllScopeSelectedValues() {
			return this.selectAllScopeItems
				.filter((item) => !!item.status)
				.map((item) => item.value);
		},
	},
	watch: {
		tableData: {
			handler() { this.buildBaseItems(); },
			deep: false
		},
		selectedValues: {
			handler(newVal, oldVal) {
				this.syncStatuses();
				// External clear (e.g. Clear Filter button) — reset search UI and rebuild full option list
				if (this._isFilterActive(oldVal) && !this._isFilterActive(newVal)) {
					this._resetTransientState();
					if (!this.isRangeType) {
						this.buildBaseItems();
					}
				}
			},
			deep: true
		},
		/**
		 * Auto-select matching items as user types in search.
		 * - Items matching search text → checked
		 * - Items NOT matching but in applied filter → stay checked (preserved)
		 * - Items NOT matching and NOT in applied filter → unchecked
		 */
		searchText(newVal) {
			const term = (newVal || '').toLowerCase().trim();
			if (!term) {
				// Search cleared — restore statuses from applied filter and reset toggles
				this.userToggled = {};
				this.syncStatuses();
				return;
			}

			const selected = Array.isArray(this.selectedValues) ? this.selectedValues : [];
			const appliedSet = new Set(selected.filter(v => v !== this.noneSentinel));

			this.baseItems.forEach(item => {
				// Skip items the user explicitly toggled — respect their choice
				if (this.userToggled[item.value]) return;

				const label = (item.label != null ? item.label : this.getFilterLabel(item.value) != null ? this.getFilterLabel(item.value) : '').toString().toLowerCase();
				if (label.includes(term)) {
					item.status = true;
				} else {
					item.status = this._isValueSelected(item.value, appliedSet, selected);
				}
			});
		},
		/**
		 * Clear search text when leaving filter view (back button)
		 */
		filterStage(newVal) {
			if (newVal === 0) {
				this.searchText = '';
			}
		},
		/**
		 * When menu opens, auto-open to filter stage if autoOpen is enabled
		 */
		isOpen(newVal, oldVal) {
			if (newVal && this.autoOpen) {
				this.filterStage = 1;
				this.userToggled = {};
				if (!this.isRangeType) {
					this.buildBaseItems();
				}
			} else if (!newVal && oldVal) {
				// Menu closed (Apply/Save/outside click) — clear search so reopen starts fresh
				this._resetTransientState();
			}
		}
	},
	mounted() {
		if (!this.isRangeType) {
			this.buildBaseItems();
		}
		// Auto-open to filter stage if autoOpen prop is true (skip Column Actions menu)
		if (this.autoOpen) {
			this.filterStage = 1;
		}
	},
	methods: {
		/**
		 * Whether a cell/filter value represents a blank (null, undefined, empty string, or empty array).
		 */
		_isBlankValue(value) {
			if (value === null || value === undefined || value === '') return true;
			return Array.isArray(value) && value.length === 0;
		},
		/**
		 * Collapse blank representations to a single canonical null for the option list.
		 */
		_normalizeFilterValue(value) {
			return this._isBlankValue(value) ? null : value;
		},
		/**
		 * Canonical key for value equivalence (blank variants, number vs string).
		 * Shared by list deduplication and v-for keys so 1 and '1' are treated as the same option.
		 */
		_getEquivalenceKey(value) {
			if (this._isBlankValue(value)) return '__blank__';
			return `__val__:${String(value)}`;
		},
		/**
		 * Deduplicate filter values using loose equivalence; keeps first raw occurrence.
		 */
		_dedupeFilterValues(values) {
			const seen = new Set();
			const result = [];
			for (const value of values) {
				const key = this._getEquivalenceKey(value);
				if (!seen.has(key)) {
					seen.add(key);
					result.push(value);
				}
			}
			return result;
		},
		/**
		 * Stable list key for v-for (null/undefined would otherwise collide).
		 */
		getItemKey(item) {
			if (!item) return '';
			return this._getEquivalenceKey(item.value);
		},
		/**
		 * Loose value match (handles number vs string from API vs table row, and blank variants).
		 */
		_valuesMatch(a, b) {
			if (a === b) return true;
			if (this._isBlankValue(a) && this._isBlankValue(b)) return true;
			if (a == null || b == null) return false;
			return this._getEquivalenceKey(a) === this._getEquivalenceKey(b);
		},
		_isValueSelected(value, selectedSet, selectedArray) {
			if (selectedSet && selectedSet.has(value)) return true;
			return (selectedArray || []).some(v => this._valuesMatch(v, value));
		},
		/**
		 * Whether selectedValues represents an active (non-default) filter
		 */
		_isFilterActive(val) {
			if (val == null) return false;
			if (Array.isArray(val)) {
				if (val.length === 0) return false;
				return true;
			}
			if (typeof val === 'object') {
				return Object.values(val).some(v => v !== null && v !== undefined && v !== '');
			}
			return false;
		},
		/**
		 * Reset search text and toggle tracking; optionally reset filter stage
		 */
		_resetTransientState({ resetStage = true } = {}) {
			this.searchText = '';
			this.userToggled = {};
			if (resetStage) {
				this.filterStage = 0;
			}
		},
		openFilter() {
			this.filterStage = 1;
			this.userToggled = {};
			if (!this.isRangeType) {
				this.buildBaseItems();
			}
		},
		/**
		 * Handle apply event from DateRangeFilter or NumberRangeFilter.
		 * Emits the range object (or null for clear) directly to parent.
		 */
		onRangeApply(val) {
			this.$emit('apply', val);
		},
		/**
		 * Build unique filter items from tableData for this column
		 */
		buildBaseItems() {
			// Range filter types do not use checkbox items
			if (this.isRangeType) return;
			this.buildPinnedItems();
			const col = this.header.value;
			const selected = Array.isArray(this.selectedValues) ? this.selectedValues.slice() : [];
			const noneSelectedMode = selected.includes(this.noneSentinel);
			const hasActiveFilter = selected.length > 0 && !noneSelectedMode;
			
			// Get unique values from tableData (filterOptionsData or tabledata from parent).
			// Include blank cells; normalize null/undefined/'' to one "(Blank)" option.
			const fromTableValues = this.tableData
				.map(row => row[col])
				.flatMap(val => {
					if (Array.isArray(val)) {
						// Keep empty arrays so they normalize to one "(Blank)" option
						return val.length === 0 ? [val] : val;
					}
					return [val];
				})
				.map(v => this._normalizeFilterValue(v));

			let candidateValues = fromTableValues.slice();
			const uniqueFromTableCount = this._dedupeFilterValues(fromTableValues).length;

			// tabledata-only mode: tableData may shrink after column filter — keep known options
			if (
				this.preserveOptionsOnFilter
				&& hasActiveFilter
				&& uniqueFromTableCount < this.baseItems.length
			) {
				this.baseItems.forEach(item => {
					candidateValues.push(this._normalizeFilterValue(item.value));
				});
			}

			// Ensure applied filter selections always appear in the list
			if (hasActiveFilter) {
				selected.forEach(v => {
					if (v !== this.noneSentinel) {
						candidateValues.push(this._normalizeFilterValue(v));
					}
				});
			}

			const pinnedValues = new Set(
				this.resolvedPinnedFilterItems.map((item) => item?.value).filter((v) => v != null)
			);

			const uniqueValues = this._dedupeFilterValues(candidateValues).filter(
				(value) => !pinnedValues.has(value)
			);

			// Build items list with status based on selectedValues
			this.baseItems = uniqueValues.map(value => {
				const label = this.getFilterLabel(value);
				let status;
				if (noneSelectedMode) {
					status = false;
				} else if (hasActiveFilter) {
					status = this._isValueSelected(value, null, selected);
				} else {
					// No filter active - all items are selected by default
					status = true;
				}
				return { value, label, status };
			});
		},
		buildPinnedItems() {
			const selected = Array.isArray(this.selectedValues) ? this.selectedValues.slice() : [];
			const noneSelectedMode = selected.includes(this.noneSentinel);
			const hasActiveFilter = selected.length > 0 && !noneSelectedMode;

			this.pinnedItems = this.resolvedPinnedFilterItems.map((item) => {
				const value = item?.value;
				let status;
				if (noneSelectedMode) {
					status = false;
				} else if (hasActiveFilter) {
					status = this._isValueSelected(value, null, selected);
				} else {
					status = true;
				}
				return {
					value,
					label: item?.label,
					sublabel: item?.sublabel,
					actor: item?.actor,
					status,
				};
			});
		},
		_getFilterSelectionPayload() {
			const selectedPinned = this.pinnedItems.filter((item) => item.status);
			const selectedBase = this.baseItems.filter((item) => item.status);
			const anySelected = selectedPinned.length > 0 || selectedBase.length > 0;
			const allBaseSelected = this.baseItems.length > 0 && this.baseItems.every((item) => item.status);
			const allPinnedSelected = this.pinnedItems.length === 0 || this.pinnedItems.every((item) => item.status);
			const allSelected = allBaseSelected && allPinnedSelected && !this.isSearchActive;

			if (!anySelected) return [this.noneSentinel];
			if (allSelected) return [];
			return [...selectedPinned, ...selectedBase].map((item) => item.value);
		},
		/**
		 * Sync item statuses with selectedValues prop
		 */
		syncStatuses() {
			// Range filter types do not use checkbox items
			if (this.isRangeType) return;
			const selected = Array.isArray(this.selectedValues) ? this.selectedValues.slice() : [];
			const noneSelectedMode = selected.includes(this.noneSentinel);
			const isFiltered = selected.length > 0 && !noneSelectedMode;
			this.pinnedItems.forEach((obj) => {
				obj.status = noneSelectedMode ? false : (!isFiltered || this._isValueSelected(obj.value, null, selected));
			});
			this.baseItems.forEach(obj => {
				obj.status = noneSelectedMode ? false : (!isFiltered || this._isValueSelected(obj.value, null, selected));
			});
		},
		toggleItem(item) {
			item.status = !item.status;
			this.$set(this.userToggled, item.value, true);
		},
		/**
		 * Shared Select-all handler (FilterSelectAllCheckbox) — scoped to visible items when searching.
		 * Hidden items retain their existing selection state.
		 * @param {Array} nextSelectedValues
		 */
		onSharedSelectAllInput(nextSelectedValues = []) {
			const selectedSet = new Set(nextSelectedValues || []);
			this.selectAllScopeItems.forEach((item) => {
				item.status = selectedSet.has(item.value);
				this.$set(this.userToggled, item.value, true);
			});
		},
		/**
		 * Toggle Select All - scoped to VISIBLE items only when searching
		 * Hidden items retain their existing selection state
		 * @param {Boolean} val - New checked state
		 */
		toggleSelectAll(val) {
			applyStatusSelectAll(this.selectAllScopeItems, !!val, (item) => {
				this.$set(this.userToggled, item.value, true);
			});
		},
		/**
		 * Handle Apply button click - emits current selection without persisting.
		 * Payload is built from ALL baseItems (visible + hidden by search).
		 */
		onApplyClick() {
			this.$emit('apply', this._getFilterSelectionPayload());
		},
		/**
		 * Handle Save button click - builds payload same as Apply, then emits save event.
		 * Payload is built from ALL baseItems (visible + hidden by search).
		 */
		onSaveClick() {
			this.$emit('save', this._getFilterSelectionPayload());
			this.onCloseClick();
		},
		/**
		 * Handle close button click or any close action.
		 * Resets filter state (search, stage) and emits close event.
		 */
		onCloseClick() {
			this._resetTransientState();
			this.$emit('close');
		},
		getFilterLabel(rawValue) {
			if (this._isBlankValue(rawValue)) {
				return '(Blank)';
			}
			const map = this.header?.filterLabelMap;
			if (map) {
				const key = typeof rawValue === 'string' ? rawValue : rawValue.toString();
				if (Object.prototype.hasOwnProperty.call(map, key)) {
					return map[key];
				}
			}
			return rawValue.toString();
		},
		formatItemLabel(item) {
			if (!item) return '';
			if (item.label != null) return item.label;
			return this.getFilterLabel(item.value);
		}
	}
};
</script>
